// user-form.schema.ts
import {AbstractControl, ValidationErrors, ValidatorFn, FormGroup} from '@angular/forms';
import {PasswordRequirement} from '../../../models/user-form.types';

export class UserFormValidator {
	static readonly passwordRequirements: PasswordRequirement[] = [
		{key: 'length', text: 'dccomp.user-form.passwordMinLength', isValid: false, check: (p: string) => p.length >= 8},
		{key: 'uppercase', text: 'dccomp.user-form.passwordUppercase', isValid: false, check: (p: string) => /[A-Z]/.test(p)},
		{key: 'lowercase', text: 'dccomp.user-form.passwordLowercase', isValid: false, check: (p: string) => /[a-z]/.test(p)},
		{key: 'number', text: 'dccomp.user-form.passwordNumber', isValid: false, check: (p: string) => /\d/.test(p)},
		{key: 'special', text: 'dccomp.user-form.passwordSpecialChar', isValid: false, check: (p: string) => /[@$!%*?&^#()_+=\-\[\]{};':"\\|,.<>\/~`]/.test(p)},
	];

	public static getPasswordRequirementsCopy(): PasswordRequirement[] {
		return [...UserFormValidator.passwordRequirements];
	}

	static passwordsMatch: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
		if (!(control instanceof FormGroup)) {
			return null; // Should be applied to a FormGroup
		}

		const password = control.get('password')?.value;
		const confirmPassword = control.get('confirmPassword')?.value;

		// Only return error if both have values and they don't match.
		// The 'required' validator handles empty fields.
		if (password && confirmPassword && password !== confirmPassword) {
			// Set error on the FormGroup level
			return {passwordsNotMatch: true};
		}

		return null; // Passwords match or one/both are empty/not yet filled
	};

	// New validator for checkbox group
	static requireAtLeastOneCheckbox: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
		if (!(control instanceof FormGroup)) {
			return null; // Should be applied to a FormGroup
		}

		let checked = 0;
		Object.keys(control.controls).forEach(key => {
			const checkboxControl = control.get(key);
			if (checkboxControl?.value === true) {
				checked++;
			}
		});

		if (checked === 0) {
			return {requireAtLeastOneCheckbox: true}; // Error key
		}

		return null; // At least one checkbox is checked
	};

	static phoneInternationalRegex = new RegExp('^\\+[1-9][\\d,-]{1,14}$');
	static phoneLocalRegex = new RegExp('^([0]\\d{1,3}[-])?\\d{7,10}$');

	static phoneNumberValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
		const phoneNumber = control.value;
		if (!phoneNumber) {
			return null; // אם אין ערך, לא נחשב שגיאה
		}
		if (phoneNumber.match(UserFormValidator.phoneInternationalRegex) || phoneNumber.match(UserFormValidator.phoneLocalRegex)) {
			return null;
		}
		return {invalidPhoneNumber: true};
	};

	// Only English letters and numbers are allowed (no special characters, no spaces)
	static readonly usernamePattern = /^[a-zA-Z0-9]+$|^[^@\s]+@[^@\s]+\.[^@\s]+$|^([0]\d{1,3}[-])?\d{7,10}$/;

	static isValidUsername(username: string): boolean {
		return UserFormValidator.usernamePattern.test(username);
	}

	static usernameValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
		const username = control.value;
		if (!username || UserFormValidator.isValidUsername(username)) {
			return null; // אם אין ערך, לא נחשב שגיאה
		}
		return {invalidUsername: true};
	};

	static phoneOrEmailRequired: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
		if (!(control instanceof FormGroup)) {
			return null;
		}

		const email = control.get('email')?.value;
		const phone = control.get('phonenumber')?.value;

		if ((!email || email.trim() === '') && (!phone || phone.trim() === '')) {
			return {phoneOrEmailRequired: true};
		}

		return null;
	};
}
